/**
 * BulletChart — Stephen Few's data-dense replacement for gauges/dials.
 *
 * Tufte endorsed it; we use it. A horizontal bar showing:
 *   - actual value (filled bar)
 *   - target line (vertical tick)
 *   - qualitative bands (faint background regions: poor / OK / good)
 *
 * Use cases:
 *   - whip-count progress vs. threshold ("we have 51, need 60, regions:
 *     0–40 doomed / 40–60 contested / 60+ safe")
 *   - quarterly fundraising vs. goal
 *   - relationship-strength tier progression (cold → cool → warm → hot)
 *   - bill momentum index against historical median
 *
 * One bullet chart fits in a stat-card footer; ten of them fit in a
 * column without a single label cluttering the design — the surrounding
 * row label is the chart's title.
 */

import React from "react";
// React 19 types no longer declare a global JSX namespace.
import type { JSX } from "react";

export interface BulletBand {
  /** Upper bound of this band on the value axis. */
  upper: number;
  /** Tailwind/CSS color class or hex. Renders as faint background. */
  fill?: string;
  /** Optional band label. Not currently rendered (Tufte: less ink). */
  label?: string;
}

export interface BulletChartProps {
  /** The actual measured value — drawn as the foreground bar. */
  value: number;
  /** The target / threshold — drawn as a vertical tick. Optional. */
  target?: number;
  /**
   * Background bands, in ascending order. Each band fills [previous.upper, this.upper].
   * E.g. [{upper: 40}, {upper: 60}, {upper: 100}] renders three regions with
   * ascending darkness (poor / fair / good convention).
   */
  bands?: BulletBand[];
  /** Domain max. Defaults to last band's upper, or value*1.2. */
  max?: number;
  /** Pixel width. Default 200. */
  width?: number;
  /** Pixel height. Default 18. */
  height?: number;
  /** Optional accessible label. */
  label?: string;
  /** Color of the actual-value bar. Default currentColor. */
  barColor?: string;
}

export function BulletChart({
  value,
  target,
  bands,
  max,
  width = 200,
  height = 18,
  label,
  barColor = "currentColor",
}: BulletChartProps): JSX.Element {
  if (!Number.isFinite(value) || value < 0) return <span className="text-xs text-stone-500">No valid nonnegative value to plot.</span>;
  const validTarget = typeof target === "number" && Number.isFinite(target) && target >= 0 ? target : undefined;
  const validBands = (Array.isArray(bands) ? bands : []).filter((band) => band && Number.isFinite(band.upper) && band.upper >= 0).slice().sort((a, b) => a.upper - b.upper);
  const domainMax = typeof max === "number" && Number.isFinite(max) && max > 0
    ? max : Math.max(1, value, validTarget ?? 0, validBands.at(-1)?.upper ?? 0);
  const padX = 2;
  const innerW = width - padX * 2;
  const xPx = (v: number): number => padX + Math.max(0, Math.min(1, v / domainMax)) * innerW;

  // Render bands. Convention: darker = better, so three bands go
  // light → medium → dark. Override via band.fill when the semantics
  // need to flip (e.g. for a "burn rate" chart where higher = worse).
  const defaultBandOpacities = [0.08, 0.15, 0.22];
  let prevUpper = 0;
  const bandRects = validBands.map((b, i) => {
    const xStart = xPx(prevUpper);
    const xEnd = xPx(b.upper);
    prevUpper = b.upper;
    const opacity = defaultBandOpacities[i] ?? 0.18;
    return (
      <rect
        key={i}
        x={xStart}
        y={0}
        width={Math.max(0, xEnd - xStart)}
        height={height}
        fill={b.fill ?? "currentColor"}
        opacity={b.fill ? undefined : opacity}
      />
    );
  });

  // Foreground bar — renders thinner than the bands so the bands stay
  // visible behind/around it.
  const barH = Math.max(4, Math.round(height * 0.4));
  const barY = (height - barH) / 2;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label ?? `Bullet chart, value ${value}${validTarget != null ? ` (target ${validTarget})` : ""}`}
    >
      {bandRects}
      <rect
        x={padX}
        y={barY}
        width={Math.max(0, xPx(value) - padX)}
        height={barH}
        fill={barColor}
      />
      {validTarget != null && (
        <line
          x1={xPx(validTarget)}
          x2={xPx(validTarget)}
          y1={1}
          y2={height - 1}
          stroke="currentColor"
          strokeWidth={2}
        />
      )}
    </svg>
  );
}
